/**
 * Side-by-side compare of two batches. Reads ?a=<id>&b=<id> from URL; lets
 * the user change either side via dropdown. Field-level diff highlighting
 * for the meta fields and ingredient delta for the table.
 */

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import * as batchesApi from '../api/batches';
import type { Batch, BatchData, IngredientRow } from '../types';
import AppHeader from '../components/AppHeader';

interface FieldLabel {
  id: keyof BatchData;
  label: string;
}

const FIELDS: FieldLabel[] = [
  { id: 'metaBatchId', label: 'Batch ID' },
  { id: 'metaProduct', label: 'Product' },
  { id: 'metaDate', label: 'Date' },
  { id: 'metaVersion', label: 'Version' },
  { id: 'metaFormulator', label: 'Formulator' },
  { id: 'metaBatchYield', label: 'Batch yield (g)' },
  { id: 'metaBottleSize', label: 'Bottle size' },
  { id: 'metaUnits', label: 'Units' },
  { id: 'metaTemp', label: 'Processing temp' },
  { id: 'metaCoolTemp', label: 'Cool-down temp' },
  { id: 'metaMixTime', label: 'Mix time' },
  { id: 'metaMixMethod', label: 'Mix method' },
  { id: 'metaEmulsify', label: 'Emulsification' },
  { id: 'metaPHTarget', label: 'pH target' },
  { id: 'metaPHMeasured', label: 'pH measured' },
  { id: 'metaPHAdjuster', label: 'pH adjuster' },
  { id: 'metaViscosity', label: 'Viscosity' },
  { id: 'metaEquipment', label: 'Equipment' },
  { id: 'metaAppearance', label: 'Appearance' },
  { id: 'metaScent', label: 'Scent' },
  { id: 'metaSkinFeel', label: 'Skin feel' },
  { id: 'batchStatus', label: 'Status' },
];

export default function ComparePage() {
  const { user } = useAuth();
  const [batches, setBatches] = useState<Batch[]>([]);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    batchesApi.listBatches().then(({ items }) => setBatches(items)).catch(() => {});
  }, []);

  const aId = params.get('a') ?? batches[0]?.id ?? '';
  const bId = params.get('b') ?? batches[1]?.id ?? batches[0]?.id ?? '';
  const a = batches.find((x) => x.id === aId) ?? null;
  const b = batches.find((x) => x.id === bId) ?? null;

  const ingredientDelta = useMemo(
    () => (a && b ? diffIngredients(a.data.rows, b.data.rows) : null),
    [a, b],
  );

  const ingredientCounts = useMemo(() => {
    if (!ingredientDelta) return null;
    let added = 0;
    let removed = 0;
    let changed = 0;
    for (const d of ingredientDelta) {
      if (d.kind === 'added') added++;
      else if (d.kind === 'removed') removed++;
      else if (d.kind === 'changed') changed++;
    }
    return { added, removed, changed };
  }, [ingredientDelta]);

  const fieldDiffCount = useMemo(() => {
    if (!a || !b) return 0;
    let n = 0;
    for (const { id } of FIELDS) {
      if (String(a.data[id] ?? '') !== String(b.data[id] ?? '')) n++;
    }
    return n;
  }, [a, b]);

  if (!user) return null;

  function setSide(side: 'a' | 'b', id: string) {
    const next = new URLSearchParams(params);
    next.set(side, id);
    setParams(next, { replace: true });
  }

  return (
    <>
      <AppHeader subtitle="Compare batches" />
      <div className="main">
        <div className="page-eyebrow">Diff</div>
        <div className="page-title">Compare batches</div>
        <div className="page-sub">
          Pick two batches to see what changed between them.
        </div>

        <div className="compare-grid">
          <div className="card">
            <div className="card-header">
              <span className="card-title">Side A</span>
            </div>
            <div className="card-body">
              <select
                className="field-select"
                value={a?.id ?? ''}
                onChange={(e) => setSide('a', e.target.value)}
              >
                <option value="">— select a batch —</option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.data.metaBatchId || b.id.slice(0, 6)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="card">
            <div className="card-header">
              <span className="card-title">Side B</span>
            </div>
            <div className="card-body">
              <select
                className="field-select"
                value={b?.id ?? ''}
                onChange={(e) => setSide('b', e.target.value)}
              >
                <option value="">— select a batch —</option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.data.metaBatchId || b.id.slice(0, 6)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {a && b && (
          <>
            <div className="card" style={{ marginTop: 12 }}>
              <div className="card-header">
                <span className="card-title">Field-by-field</span>
                <span style={{ fontSize: 11, color: 'var(--ink3)' }}>
                  {fieldDiffCount === 0
                    ? 'no differences'
                    : `${fieldDiffCount} field${fieldDiffCount === 1 ? '' : 's'} differ`}
                </span>
              </div>
              <div className="card-body">
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--ink3)' }}>Field</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--ink3)' }}>A</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--ink3)' }}>B</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FIELDS.map(({ id, label }) => {
                      const av = String(a.data[id] ?? '');
                      const bv = String(b.data[id] ?? '');
                      const same = av === bv;
                      return (
                        <tr key={id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '6px 8px', color: 'var(--ink2)' }}>{label}</td>
                          <td style={{ padding: '6px 8px' }} className={!same && av ? 'diff-del' : ''}>
                            {av || '—'}
                          </td>
                          <td style={{ padding: '6px 8px' }} className={!same && bv ? 'diff-add' : ''}>
                            {bv || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {ingredientDelta && ingredientCounts && (
              <div className="card">
                <div className="card-header">
                  <span className="card-title">Ingredient delta</span>
                  <span style={{ fontSize: 11, color: 'var(--ink3)' }}>
                    <span style={{ color: 'var(--sage)' }}>+{ingredientCounts.added} added</span>
                    <span style={{ margin: '0 6px' }}>·</span>
                    <span style={{ color: 'var(--rose)' }}>−{ingredientCounts.removed} removed</span>
                    <span style={{ margin: '0 6px' }}>·</span>
                    <span style={{ color: 'var(--amber)' }}>{ingredientCounts.changed} changed</span>
                  </span>
                </div>
                <div className="card-body">
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--ink3)' }}>Ingredient</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--ink3)' }}>A (g)</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--ink3)' }}>B (g)</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--ink3)' }}>Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ingredientDelta.map((d, i) => (
                        <tr key={i} className={d.kind === 'added' ? 'diff-add' : d.kind === 'removed' ? 'diff-del' : d.kind === 'changed' ? 'diff-mod' : ''}>
                          <td style={{ padding: '6px 8px' }}>{d.name}</td>
                          <td style={{ padding: '6px 8px' }}>{d.aAmount ?? '—'}</td>
                          <td style={{ padding: '6px 8px' }}>{d.bAmount ?? '—'}</td>
                          <td style={{ padding: '6px 8px' }}>{d.delta ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

interface IngredientDiff {
  name: string;
  kind: 'added' | 'removed' | 'changed' | 'same';
  aAmount: string | null;
  bAmount: string | null;
  delta: string | null;
}

function diffIngredients(aRows: IngredientRow[], bRows: IngredientRow[]): IngredientDiff[] {
  const key = (r: IngredientRow) =>
    (r.ingredient.trim() + '|' + (r.inci || '')).toLowerCase();
  const aMap = new Map<string, IngredientRow>();
  for (const r of aRows) if (r.ingredient.trim()) aMap.set(key(r), r);
  const bMap = new Map<string, IngredientRow>();
  for (const r of bRows) if (r.ingredient.trim()) bMap.set(key(r), r);

  const allKeys = new Set([...aMap.keys(), ...bMap.keys()]);
  const out: IngredientDiff[] = [];
  for (const k of allKeys) {
    const a = aMap.get(k);
    const b = bMap.get(k);
    if (a && !b) {
      out.push({
        name: a.ingredient,
        kind: 'removed',
        aAmount: a.amount || '0',
        bAmount: null,
        delta: '−' + (a.amount || '0'),
      });
    } else if (!a && b) {
      out.push({
        name: b.ingredient,
        kind: 'added',
        aAmount: null,
        bAmount: b.amount || '0',
        delta: '+' + (b.amount || '0'),
      });
    } else if (a && b) {
      const aNum = parseFloat(a.amount) || 0;
      const bNum = parseFloat(b.amount) || 0;
      const diff = bNum - aNum;
      out.push({
        name: a.ingredient || b.ingredient,
        kind: Math.abs(diff) > 0.0001 ? 'changed' : 'same',
        aAmount: a.amount || '0',
        bAmount: b.amount || '0',
        delta:
          Math.abs(diff) > 0.0001 ? (diff > 0 ? '+' : '') + diff.toFixed(2) : '0',
      });
    }
  }
  // Sort: added first, then changed, then removed, then same.
  const rank: Record<IngredientDiff['kind'], number> = {
    added: 0,
    changed: 1,
    removed: 2,
    same: 3,
  };
  out.sort((a, b) => rank[a.kind] - rank[b.kind] || a.name.localeCompare(b.name));
  return out;
}
